import { miner } from '@cbre/digger';
import { areaUomAbbrev, formatNumber, UserSettings } from '@miq/utils';
import { CatalogResponse, Column, LeaseColumns, LeaseSections } from '@miq/catalog';
import moment from 'moment';
import { RecordTimestamp } from '../../models/dates.models';
import { SuiteType, UserSettingsPreference } from '../../stacking-plan-container/floor-container/floor/item/item.model';
import { MappedLov, AskingRateRangeObject } from '../../stacking-plan.model';
import { MY_FORMATS, UTC_EDP_INPUT_FORMAT } from '../constants/date.const';
import { SettingsLeaseType } from '../../stacking-plan-container/actions-toolbar/settings/settings.model';

const MASKING_TOKEN = '*****';
const EMPTY_STRING = '';

export const symbolRetriever = currency => {
  return !currency ? EMPTY_STRING : '$';
};

export function isNotNullOrEmptyString(value: string | number): boolean {
  return value != null && `${value}`.trim() !== '';
}

export function sanitizeValueOr(alternative: string | null, value: string | number): string | number {
  return isNotNullOrEmptyString(value) ? value : alternative;
}

export function sanitizeValueOrNull(value: string | number): string | number {
  return sanitizeValueOr(null, value);
}

export function valueAndUOMFormatter(
  valueKey: string,
  uomKey: string,
  object: { [key: string]: number | any },
  uomLov?: MappedLov
): string {
  let result = null;
  if (object) {
    const value =
      object[valueKey] != null && typeof object[valueKey] === 'function' ? object[valueKey]() : object[valueKey];
    const uom = areaUomAbbrev(
      object[uomKey] != null && typeof object[uomKey] === 'function' ? object[uomKey]() : object[uomKey]
    );
    result = formatValueWithItsUom(value, uom, uomLov);
  }
  return result;
}

function formatValueWithItsUom(value: number | any, uom: number | any, uomLov?: MappedLov): string {
  if (isNotNullOrEmptyString(value) && isNotNullOrEmptyString(uom)) {
    if (uomLov != null) {
      const uomOption = uomLov[uom];
      uom = uomOption != null ? uomOption : uom;
    }
    return `${formatNumber(value)} ${uom}`;
  } else if (isNotNullOrEmptyString(value) && !isNotNullOrEmptyString(uom)) {
    return `${formatNumber(value)}`;
  }
  return null;
}

export function valueAndUOMFormatterBinder(valueKey: string, uomKey: string, object: { [key: string]: number | any }) {
  return valueAndUOMFormatter.bind(null, valueKey, uomKey, object);
}
export function formatDate(date: string | Date, format = MY_FORMATS): string {
  if (isMasked(date)) {
    return date as string;
  }
  return date ? moment.utc(date, UTC_EDP_INPUT_FORMAT).format(format) : null;
}

export function generateLastUpdatedByMessage(updatedDate: string, updatedBy: string): RecordTimestamp {
  let result: RecordTimestamp = null;
  if (isNotNullOrEmptyString(updatedDate) && isNotNullOrEmptyString(updatedBy)) {
    result = { lastUpdatedDate: formatDate(updatedDate), lastUpdatedBy: updatedBy };
  }
  return result;
}

// TODO: might move this method to utils library
export const stringifyPostalCodeAddress = (postalAddress, defaultText = '-', addNewLine = false) => {
  if (!postalAddress) {
    return 'None';
  }
  const postalAddressSubset = {
    streetName: 'full_street_name',
    city: 'city',
    state: 'state_province',
    zip: 'postal_code',
  };
  const { streetName = '', city = '', state = '', zip = '' } = miner(postalAddress, postalAddressSubset);
  const fullAddress = `${[streetName, city, state].filter(w => !!w).join(', ')} ${zip}`;
  if (addNewLine) {
    const [first, ...rest] = [streetName, city, state].filter(w => !!w);
    const fullAddressWithNewLine = `${first + '\n' + rest.join(', ')} ${zip}`;
    return !!fullAddressWithNewLine ? fullAddressWithNewLine : defaultText;
  }
  return !!fullAddress ? fullAddress : defaultText;
};

// Security: use constant-time comparison to prevent timing side-channel attacks
// that could reveal the masking token value through measurable response-time differences.
export const isMasked = (x: unknown): boolean => {
  const a = String(x);
  if (a.length !== MASKING_TOKEN.length) return false;
  let result = 0;
  for (let i = 0; i < MASKING_TOKEN.length; i++) {
    result |= a.charCodeAt(i) ^ MASKING_TOKEN.charCodeAt(i);
  }
  return result === 0;
};
export function formatNumbers(value: string, digitalInfo: string) {
  if (!value || typeof value !== 'string') return null;

  const parsedValue = parseFloat(value.replace(/,/g, ''));
  if (parsedValue == null || isNaN(parsedValue) || parsedValue < 0) return null;
  return formatNumber(parsedValue, 'en-US', digitalInfo);
}
export function isTenantEntityActive(): boolean {
  const totalEntities = JSON.parse(localStorage.getItem('totalEntities')) || [];
  if (totalEntities.length > 0) {
    return totalEntities.some(a => a.entityObj.name === 'Tenancy' && a.entityObj.isChecked);
  }
  return false;
}

export function isOpportunityTabActive(): boolean {
  const totalEntities = JSON.parse(localStorage.getItem('totalEntities')) || [];
  if (totalEntities.length > 0) {
    const lease = totalEntities.find(a => a.entityObj.name === 'Lease' && a.entityObj.isChecked);
    return lease && lease?.subTabList.some(a => a.name === 'Opportunities' && a.isChecked);
  }
  return false;
}

export function getTranslationInput(key: string): number | string {
  const currentYear = moment().year();
  switch (key) {
    case 'filters.leaseFilters.1orLess':
      return currentYear;
    case 'filters.leaseFilters.1To2':
      return currentYear + 1;
    case 'filters.leaseFilters.2To3':
      return currentYear + 2;
    case 'filters.leaseFilters.3To4':
      return currentYear + 3;
    case 'filters.leaseFilters.4To5':
      return currentYear + 4;
    case 'filters.leaseFilters.5To6':
      return currentYear + 5;
    case 'filters.leaseFilters.6To7':
      return currentYear + 6;
    case 'filters.leaseFilters.7To8':
    case 'filters.leaseFilters.8ToMore':
      return currentYear + 7;
    default:
      return key;
  }
}

export const findOwnerOccupiedColumn = (sections: CatalogResponse<Column>, lookForColumn: LeaseColumns) =>
  sections[SuiteType.Lease]
    .find(section => section.sectionId === LeaseSections.LeaseCompDetails)
    ?.columns.some(column => column.columnId === lookForColumn);

export function generateRateRange(rateObject: AskingRateRangeObject, isMonthly: boolean): string {
  const { minAskingRate, maxAskingRate, minAskingUom, maxAskingUom } = rateObject;
  // At least one field contains a value
  if (isNotNullOrEmptyString(minAskingRate) || isNotNullOrEmptyString(maxAskingRate)) {
    const monthlyYearlyAbrev = isMonthly ? 'Mo' : 'Yr';
    const currencySymbol = symbolRetriever(minAskingUom ?? maxAskingUom);

    const minValue = !!minAskingRate ? formatNumber(minAskingRate, undefined, '.2-2') : '-';
    const maxValue = !!maxAskingRate ? formatNumber(maxAskingRate, undefined, '.2-2') : '-';
    if (minValue === maxValue) {
      return `${currencySymbol}${minValue} ${monthlyYearlyAbrev}`;
    }
    return `${currencySymbol}${minValue} - ${maxValue} ${monthlyYearlyAbrev}`;
  }
  return '-';
}

export function getUserRatePrefs(): UserSettingsPreference {
  const rents = UserSettings.getRentConfiguration();
  return rents?.length ? rents[0].value?.toLowerCase() : UserSettingsPreference.Yearly;
}

export function processRateOpts(leaseRateOpts: string): string {
  if (!leaseRateOpts) {
    return null;
  }
  const leaseOpts = Object.values(SettingsLeaseType).map(opt => opt as string);

  return leaseOpts.includes(leaseRateOpts) ? leaseRateOpts : null;
}
